"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { User, Clock, ArrowRight, UserCheck, Users, AlertCircle } from "lucide-react";
import { formatTimeAgo, getPriorityLabel, getPriorityColor } from "@/lib/time-ago";
import type { CamundaTask } from "@/lib/camunda";

interface TaskCardTituloProps {
  text: string | null;
}

function TaskCardTitulo({ text }: TaskCardTituloProps) {
  if (text == null || text === "") {
    return null;
  }
  return (
    <div className="mb-3 text-sm text-gray-700">
      <span className="font-bold text-gray-900">Título:</span>
      <span className="ml-1">{text}</span>
    </div>
  );
}

interface TaskCardProps {
  task: CamundaTask;
  titulo: string | null;
  currentUserId?: string;
  onClick?: (taskId: string) => void;
  onPrefetch?: (taskId: string) => void;
  showSeparator?: boolean;
}

export function TaskCard({
  task,
  titulo,
  currentUserId,
  onClick,
  onPrefetch,
  showSeparator = true,
}: TaskCardProps) {
  const assignmentStatus = task.assignee
    ? task.assignee === currentUserId
      ? {
          type: "assigned-to-me" as const,
          label: `${task.assignee}`,
          icon: UserCheck,
          color: "bg-green-100 text-green-800 border-green-200",
        }
      : {
          type: "assigned-to-other" as const,
          label: `${task.assignee}`,
          icon: User,
          color: "bg-blue-100 text-blue-800 border-blue-200",
        }
    : {
        type: "unassigned" as const,
        label: "Sin asignar",
        icon: AlertCircle,
        color: "bg-gray-100 text-gray-800 border-gray-200",
      };

  const StatusIcon = assignmentStatus.icon;

  const borderLeftColor = (() => {
    switch (assignmentStatus.type) {
      case "assigned-to-me":
        return "#eab308";
      case "assigned-to-other":
        return "#3b82f6";
      case "unassigned":
        return "#6b7280";
      default:
        return "#6b7280";
    }
  })();

  const handleCardClick = () => {
    onClick?.(task.id);
  };

  const handleIntent = () => {
    onPrefetch?.(task.id);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(task.id);
  };

  return (
    <>
      <div
        className="border-l-4 p-4 transition-colors hover:bg-gray-50"
        style={{
          borderLeftColor,
        }}
        onFocus={handleIntent}
        onMouseEnter={handleIntent}
      >
        <div className="pb-3">
          <div className="flex justify-between items-start">
            <button
              type="button"
              className="text-left text-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Abrir tarea ${task.name}`}
              onClick={handleCardClick}
              onFocus={handleIntent}
              onMouseEnter={handleIntent}
            >
              {task.name}
            </button>
            <div className="flex gap-2">
              <Badge className={`${getPriorityColor(task.priority)}`}>
                {getPriorityLabel(task.priority)}
              </Badge>
            </div>
          </div>

          <div
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border mt-2 ${assignmentStatus.color}`}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {assignmentStatus.label}
          </div>
        </div>

        <TaskCardTitulo text={titulo} />

        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>{formatTimeAgo(task.created)}</span>
            </div>

            {!task.assignee && task.candidateGroups && task.candidateGroups.length > 0 ? (
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                <span className="text-xs">{task.candidateGroups.join(", ")}</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleButtonClick}
              onFocus={handleIntent}
              onMouseEnter={handleIntent}
            >
              {task.assignee === currentUserId ? "Trabajar" : "Ver tarea"}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {showSeparator ? <Separator className="h-px bg-gray-200" /> : null}
    </>
  );
}
